import { useState, useRef, useEffect, useMemo } from "react";
import type { EventPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { COURT_COLORS } from "@/lib/scheduler-utils";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

interface CourtsManagerProps {
  event: EventPublic;
  canEdit: boolean;
  courtLabel: string;
  hostSchoolCourts: { id: string; name: string }[];
  setEvent: React.Dispatch<React.SetStateAction<EventPublic | null>>;
}

export function CourtsManager({ event, canEdit, courtLabel, hostSchoolCourts, setEvent }: CourtsManagerProps) {
  const [search, setSearch] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 194 -- fields added here now file under a venue. Single/zero-venue events
  // rely on the server default (primary, or null when the event has none), so
  // no picker is needed. Only when the event has more than one venue do we ask
  // which one, defaulting to the primary (venues are sorted primary-first).
  const primaryVenueId = event.venues.find((v) => v.is_primary)?.id ?? event.venues[0]?.id ?? "";
  const multiVenue = event.venues.length > 1;
  const [venueChoice, setVenueChoice] = useState<string>("");
  const chosenVenueId = venueChoice || primaryVenueId;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDrop(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // 087: show the full list of host-school courts when dropdown is open with no
  // search; filter as the user types. Already-assigned courts are excluded.
  const filtered = useMemo(() => {
    const existingIds = new Set(event.courts.map((c) => c.court_id).filter((id): id is string => id !== null));
    const available = hostSchoolCourts.filter((c) => !existingIds.has(c.id));
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter((c) => c.name.toLowerCase().includes(q));
  }, [search, hostSchoolCourts, event.courts]);

  // Ad-hoc is allowed when the typed name matches no existing managed court
  // and isn't already added (by name) to this event.
  const canAddAdhoc = useMemo(() => {
    const q = search.trim();
    if (!q) return false;
    const nameLower = q.toLowerCase();
    const existingNames = new Set(event.courts.map((c) => c.name.toLowerCase()));
    if (existingNames.has(nameLower)) return false;
    const hasExactManagedMatch = hostSchoolCourts.some((c) => c.name.toLowerCase() === nameLower);
    return !hasExactManagedMatch;
  }, [search, hostSchoolCourts, event.courts]);

  // 194 -- pass the chosen venue only when there's a genuine choice; otherwise
  // let the server default it (primary, or null for a venue-less event).
  const venueField = multiVenue && chosenVenueId ? { event_venue_id: chosenVenueId } : {};

  async function add(courtId: string) {
    setSearch("");
    setShowDrop(false);
    const res = await api.addEventCourt(event.id, { court_id: courtId, ...venueField });
    setEvent((prev) => prev ? { ...prev, courts: [...prev.courts, res.court] } : prev);
  }

  async function addAdhoc(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSearch("");
    setShowDrop(false);
    const res = await api.addEventCourt(event.id, { name: trimmed, ...venueField });
    setEvent((prev) => prev ? { ...prev, courts: [...prev.courts, res.court] } : prev);
  }

  async function remove(entryId: string) {
    setEvent((prev) => prev ? { ...prev, courts: prev.courts.filter((c) => c.id !== entryId) } : prev);
    await api.removeEventCourt(event.id, entryId);
  }

  return (
    <div className="min-w-0 flex-1" data-region="courts">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <h3 className="shrink-0 text-sm font-medium text-muted-foreground">{courtLabel}s</h3>
        {canEdit && multiVenue && (
          <select
            aria-label={`Venue for new ${courtLabel.toLowerCase()}`}
            data-field="court-venue"
            value={chosenVenueId}
            onChange={(e) => setVenueChoice(e.target.value)}
            className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
          >
            {event.venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.display_name}{v.is_primary ? " (primary)" : ""}
              </option>
            ))}
          </select>
        )}
        {canEdit && (
          <div ref={ref} className="relative min-w-0 flex-1 max-w-xs">
            <Input value={search} onChange={(e) => { setSearch(e.target.value); setShowDrop(true); }}
              onFocus={() => setShowDrop(true)} /* 087: always open on focus so the user sees every court */
              placeholder={`Add ${courtLabel.toLowerCase()}...`} className="h-7 text-xs" data-field="court-search"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (filtered.length === 1) { add(filtered[0].id); return; }
                  if (filtered.length === 0 && canAddAdhoc) { addAdhoc(search); return; }
                }
                if (e.key === "Escape") setShowDrop(false);
              }}
            />
            {showDrop && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-md border border-input bg-background shadow-md">
                {filtered.map((c) => (
                  <button key={c.id} type="button" onClick={() => add(c.id)}
                    className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted">{c.name}</button>
                ))}
                {search.trim() && filtered.length === 0 && canAddAdhoc && (
                  <button type="button" onClick={() => addAdhoc(search)}
                    className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted" data-action="add-adhoc-court">
                    <span className="text-muted-foreground">Create ad-hoc {courtLabel.toLowerCase()}:</span>
                    <span className="ml-1 font-medium">{search.trim()}</span>
                  </button>
                )}
                {search.trim() && filtered.length === 0 && !canAddAdhoc && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Already added</div>
                )}
                {!search.trim() && filtered.length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">No {courtLabel.toLowerCase()}s available -- type a name to create ad-hoc</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {[...event.courts].filter((c) => !c.event_venue_id).sort((a, b) => a.name.localeCompare(b.name)).map((c, ci) => {
          const cc = COURT_COLORS[ci % COURT_COLORS.length];
          const borderStyle = c.is_adhoc ? "border-dashed" : "";
          return (
            <span key={c.id} data-item="court" data-item-id={c.id} data-adhoc={c.is_adhoc ? "true" : undefined} className={`group/chip inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium whitespace-nowrap ${cc.bg} ${cc.border} ${cc.text} ${borderStyle}`} title={c.is_adhoc ? `Ad-hoc ${courtLabel.toLowerCase()} -- only for this event` : undefined}>
              {c.name}
              {canEdit && (
                <button type="button" title={`Remove ${c.name}`} onClick={() => remove(c.id)} data-action="remove-court" className="hidden hover:text-destructive group-hover/chip:inline-flex"><X className="h-3 w-3" /></button>
              )}
            </span>
          );
        })}
        {event.courts.filter((c) => !c.event_venue_id).length === 0 && <span className="text-xs text-muted-foreground">No {courtLabel.toLowerCase()}s assigned</span>}
      </div>
    </div>
  );
}
